/**
 * Occupational Therapy Re-evaluation Note — options + empty payload.
 * After shared common header (1–6): Re-eval Info → Changes → Assessment
 * table (grouped) → Goals → Skilled Need → Plan of Care → Discharge
 * (per OT Re-evaluation Visit Note screenshots).
 */

import { emptyOtCommonHeader } from "./otNotesCommonHeaderUtils";
import {
  emptyProgressGoalRow,
  emptyProgressGoals,
} from "../ptNotes/ptGoals";

export const REEVAL_REASON_OPTIONS = [
  "Routine (Per Plan of Care)",
  "Change in Condition",
  "Fall",
  "Post Hospitalization",
  "Post ER Visit",
  "Other",
];

/** Row-major order so a 3-column wrap matches the screenshot columns. */
export const CHANGES_SINCE_LAST_OPTIONS = [
  "New Diagnosis",
  "Hospitalization",
  "ER Visit",
  "Change in Medication",
  "Change in Functional Status",
  "New Fall",
  "Change in Assistive Device",
  "Change in Home Environment",
  "Caregiver Change",
];

export const CHANGES_SINCE_LAST_ALIASES = {
  "New Fall / Near Fall": "New Fall",
  "Change in Adaptive Equipment": "Change in Assistive Device",
};

export const ASSESSMENT_GROUPS = [
  {
    label: "ADL Performance",
    areas: ["Bathing", "Dressing (UB/LB)", "Grooming", "Feeding / Eating"],
  },
  {
    label: "IADL Performance",
    areas: [
      "Meal Prep",
      "Housekeeping",
      "Shopping / Errands",
      "Medication Management",
      "Financial Management",
    ],
  },
  {
    label: "Upper Extremity Function",
    areas: ["ROM", "Strength", "Fine Motor / Coordination"],
  },
  {
    label: "Cognition & Safety",
    areas: ["Orientation", "Safety Awareness"],
  },
];

export const ASSESSMENT_AREA_OPTIONS = ASSESSMENT_GROUPS.flatMap(
  (group) => group.areas,
);

export const ASSESSMENT_AREA_ALIASES = {
  "Bathing / Showering": "Bathing",
  "Upper Body Dressing": "Dressing (UB/LB)",
  "Meal Preparation / IADLs": "Meal Prep",
  "Upper Extremity ROM (AROM)": "ROM",
  "Upper Extremity Strength (MMT)": "Strength",
  "Cognition / Safety Awareness": "Safety Awareness",
};

export const CHANGE_OPTIONS = ["Improved", "Declined", "No Change"];

export const GOAL_STATUS_OPTIONS = ["Met", "Partially Met", "Not Met"];

export const YES_NO_OPTIONS = ["Yes", "No"];

export const PLAN_OF_CARE_STATUS_OPTIONS = [
  "Continue Without Changes",
  "Modify Plan of Care",
  "Decrease Frequency",
  "Increase Frequency",
  "New Physician Orders Requested",
  "Discharge Planning Initiated",
];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const today = () => new Date().toISOString().slice(0, 10);

export const emptyAssessmentRow = (area) => ({
  area,
  lastAssessment: "",
  currentAssessment: "",
  change: "",
});

export { emptyProgressGoalRow as emptyGoalRow, emptyProgressGoals as emptyGoals };

export const emptyAssessmentFindings = () =>
  ASSESSMENT_AREA_OPTIONS.map((area) => emptyAssessmentRow(area));

/** Full editable OT Re-evaluation Note payload (JSON on dedicated row). */
export const emptyOtReevalNotePayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  visitDate: "",
  commonHeader: emptyOtCommonHeader(),

  // —— 7. Re-evaluation Information ——
  reevalReasons: [],
  reevalReasonOther: "",

  // —— 8. Changes Since Last Assessment ——
  changesSinceLast: [],
  changesComments: "",

  // —— 9. Current OT Assessment (grouped, vs last) ——
  assessmentFindings: emptyAssessmentFindings(),

  // —— 10. Progress Toward Goals ——
  goals: emptyProgressGoals(3),
  additionalGoalsAttached: false,

  // —— 11. Skilled OT Need ——
  skilledOtNeed: "",

  // —— 12. Plan of Care / Orders ——
  planOfCareStatus: [],
  planOfCareComments: "",

  // —— 13. Discharge Planning ——
  dischargeAnticipated: "",
  dischargeExpectedDate: "",
  dischargeNeedsPlan: "",
});

export const OT_REEVAL_ARRAY_KEYS = [
  "reevalReasons",
  "changesSinceLast",
  "planOfCareStatus",
];
